import { Button } from "components/DesignSystem/Button/Button";
import { CheckboxField } from "components/DesignSystem/Checkbox/CheckboxField";
import { BareInput } from "components/DesignSystem/Input/Input";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useToast } from "hooks/useToast";
import { useUpdatePhysicalNexusForStateMutation } from "store/apis/salesTax";
import { BackendError } from "types/utils/error";

type StatePhysicalPresenceValues = {
  office_present: boolean;
  warehouse_present: boolean;
  employees_present: boolean;
};

const initialValues: StatePhysicalPresenceValues = {
  office_present: false,
  warehouse_present: false,
  employees_present: false,
};

type StatePhysicalPresenceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  stateId: string;
  stateName: string;
  onSuccess?: () => void;
};

export const StatePhysicalPresenceModal = ({
  isOpen,
  onClose,
  entityId,
  stateId,
  stateName,
  onSuccess,
}: StatePhysicalPresenceModalProps) => {
  const { alertToast } = useToast();
  const [updatePhysicalNexusForState, { isLoading }] =
    useUpdatePhysicalNexusForStateMutation();

  const handleSubmit = async (values: StatePhysicalPresenceValues) => {
    try {
      await updatePhysicalNexusForState({
        entityId,
        stateId,
        payload: values,
      }).unwrap();
      onSuccess?.();
      onClose();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Modal.Content size="regular" useCustomOverlay>
        <Modal.Header>
          <Modal.Title>Physical presence</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <FormikForm initialValues={initialValues} onSubmit={handleSubmit}>
          {({ values }) => {
            const hasSelectedAtLeastOne =
              values.office_present ||
              values.warehouse_present ||
              values.employees_present;

            return (
              <>
                <Modal.Body>
                  <div className="t-flex t-flex-col t-gap-4">
                    <BareInput
                      label="State"
                      value={stateName}
                      disabled
                      readOnly
                    />
                    <CheckboxField
                      name="office_present"
                      checkboxProps={{
                        label: `We have an office in ${stateName}`,
                      }}
                    />
                    <CheckboxField
                      name="warehouse_present"
                      checkboxProps={{
                        label: `We have a warehouse in ${stateName}`,
                      }}
                    />
                    <CheckboxField
                      name="employees_present"
                      checkboxProps={{
                        label: `We have employees working in ${stateName}`,
                      }}
                    />
                  </div>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Button onClick={onClose} type="button">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    customType="primary"
                    isLoading={isLoading}
                    disabled={isLoading || !hasSelectedAtLeastOne}
                  >
                    Save
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
